import { useMemo, useState } from 'react';
import { Wand2 } from 'lucide-react';
import {
  Button, Textarea, Card, CardHeader, CardTitle, CardDescription, CardBody,
  Combobox, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, type ComboboxOption,
} from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { useOpsOrgs } from '@/api/operations-org-hooks';
import { useOpsServices } from '@/api/operations';
import {
  useOpsOnboardingGaps,
  useAttachOnboardingGap,
  useCreateSecondaryService,
  resolveDomainOrg,
  type OpsOnboardingGap,
  type ResolveDomainOrgResult,
} from '@/api/operations-cdn-gaps';

type ProviderFilter = '' | 'eagle' | 'raven' | 'hawk';
// Select needs non-empty string values; 'all' maps to the '' (no filter) provider.
const PROVIDER_OPTIONS = [
  { value: 'all', label: 'All providers' },
  { value: 'eagle', label: 'Eagle' },
  { value: 'raven', label: 'Raven' },
  { value: 'hawk', label: 'Hawk' },
];

function gb(bytes: number): string {
  return (bytes / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const ACTION_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  attach: 'success',
  'create-secondary': 'warning',
  'needs-service': 'neutral',
};

/** Split a free-text blob into domains (newline / comma / space separated). */
function parseDomains(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,]+/)
        .map((d) => d.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''))
        .filter(Boolean),
    ),
  );
}

/**
 * CDN → Onboarding Gaps tab.
 *
 * Two parts:
 *  1. Onboard form — pick org → pick CDN service → enter one or more domains →
 *     Attach (bind-only: registers domain_services rows so the analytics sync
 *     starts pulling; never touches the vendor). Works for ANY domain, not only
 *     detector-found ones.
 *  2. Detected gaps — fleet list from cdn_analytics_onboarding_gaps. "Prefill"
 *     drops a row's domain + resolved org/service into the form above. Rows whose
 *     family lacks the gap provider show `create-secondary` (needs a service
 *     created first — not yet supported in this form).
 */
export function CdnOnboardingGapsTab() {
  /* ── Onboard form state ──────────────────────────────────────────────── */
  const [orgQuery, setOrgQuery] = useState('');
  const [orgId, setOrgId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [domainsText, setDomainsText] = useState('');
  const [banner, setBanner] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  // Owner hint for gaps the detector could not resolve (Raven/Hawk): filled on
  // Prefill from /cdn/resolve-org (sibling-root match). Display-only — the
  // operator still verifies the customer in DataCentral and picks org manually.
  const [orgHint, setOrgHint] = useState<ResolveDomainOrgResult | null>(null);
  const [orgHintLoading, setOrgHintLoading] = useState(false);

  const orgsQ = useOpsOrgs({ q: orgQuery || undefined, per_page: 20 });
  const orgs = orgsQ.data?.results ?? [];
  const orgOptions: ComboboxOption[] = useMemo(
    () =>
      orgs.map((o) => ({
        value: o.organization_id,
        label: o.customer?.customer_name
          ? `${o.customer.customer_name} (${o.organization_id})`
          : o.organization_id,
      })),
    [orgs],
  );

  const servicesQ = useOpsServices(orgId ? { org_id: Number(orgId), category: 'cdn', per_page: 100 } : {});
  const services = useMemo(
    () => (orgId ? (servicesQ.data?.results ?? []).filter((s) => s.organization_id === orgId) : []),
    [servicesQ.data, orgId],
  );
  const serviceOptions: ComboboxOption[] = useMemo(
    () =>
      services.map((s) => ({
        value: s.id,
        label: (s.provider?.name ? `${s.provider.name} · ` : '') + s.service_name,
      })),
    [services],
  );

  const attach = useAttachOnboardingGap();
  const createSvc = useCreateSecondaryService();

  // org for a gap row: detector value, else first org in existing_bindings ("Eagle:16606").
  const rowOrg = (g: OpsOnboardingGap) =>
    g.organization_id || g.existing_bindings.split(',')[0]?.split(':')[1]?.trim() || '';

  // create-secondary: provision the missing provider service for the resolved org,
  // then bind. Opens a design-system ConfirmDialog (csTarget) before the write.
  const [csTarget, setCsTarget] = useState<OpsOnboardingGap | null>(null);

  const askCreateSecondary = (g: OpsOnboardingGap) => {
    if (!rowOrg(g)) {
      setBanner({ tone: 'danger', text: `No org resolved for ${g.domain} — Prefill + pick the org manually first.` });
      return;
    }
    setBanner(null);
    setCsTarget(g);
  };

  const confirmCreateSecondary = async () => {
    if (!csTarget) return;
    const g = csTarget;
    const org = rowOrg(g);
    try {
      await createSvc.mutateAsync({ domain: g.domain, organization_id: org, provider: g.provider });
      setBanner({ tone: 'success', text: `Created ${g.provider} service for org ${org} and bound ${g.domain}.` });
    } catch {
      setBanner({ tone: 'danger', text: `Create service failed for ${g.domain}.` });
    } finally {
      setCsTarget(null);
    }
  };

  const domains = parseDomains(domainsText);
  const canSubmit = domains.length > 0 && serviceId !== '' && !attach.isPending;

  const handleSubmit = async () => {
    setBanner(null);
    let ok = 0;
    const failed: string[] = [];
    for (const domain of domains) {
      try {
        await attach.mutateAsync({ domain, service_id: serviceId });
        ok += 1;
      } catch {
        failed.push(domain);
      }
    }
    if (failed.length === 0) {
      setBanner({ tone: 'success', text: `Attached ${ok} domain(s).` });
      setDomainsText('');
    } else {
      setBanner({ tone: 'danger', text: `Attached ${ok}, failed ${failed.length}: ${failed.join(', ')}` });
    }
  };

  /* ── Detected gaps ───────────────────────────────────────────────────── */
  const [provider, setProvider] = useState<ProviderFilter>('');
  const gapsQ = useOpsOnboardingGaps(provider || undefined);
  const rows = gapsQ.data ?? [];

  const prefill = (g: OpsOnboardingGap) => {
    setDomainsText(g.domain);
    // org: detector value (eagle) or the first org in existing_bindings ("Eagle:12519, …").
    const org = g.organization_id || g.existing_bindings.split(',')[0]?.split(':')[1]?.trim() || '';
    if (org) {
      setOrgQuery(org);
      setOrgId(org);
      setOrgHint(null);
    } else {
      // Raven/Hawk gap with no attribution → fetch a sibling-root owner hint.
      // Do NOT auto-fill the org: the operator confirms the customer in
      // DataCentral and picks it manually.
      setOrgHint(null);
      setOrgHintLoading(true);
      resolveDomainOrg(g.domain)
        .then((r) => setOrgHint(r))
        .catch(() => setOrgHint(null))
        .finally(() => setOrgHintLoading(false));
    }
    setServiceId(g.suggested_service_id || '');
    setBanner(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnDef<OpsOnboardingGap>[] = [
    { id: 'provider', header: 'Provider', cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.provider}</span> },
    { id: 'domain', header: 'Domain', accessorKey: 'domain' },
    { id: 'volume', header: 'Volume (GB)', cell: ({ row }) => <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{gb(row.original.volume_bytes)}</span> },
    {
      id: 'target',
      header: 'Suggested org / service',
      cell: ({ row }) => {
        const g = row.original;
        return (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{g.suggested_service_name || g.organization_id || '—'}</span>
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {g.suggested_service_name ? `org ${g.organization_id}` : g.existing_bindings || 'no binding'}
            </span>
          </div>
        );
      },
    },
    { id: 'action_hint', header: 'Action', cell: ({ row }) => <StatusPill tone={ACTION_TONE[row.original.suggested_action] ?? 'neutral'}>{row.original.suggested_action}</StatusPill> },
    {
      id: 'prefill',
      header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
          <Button variant="ghost" size="sm" startIcon={<Wand2 size={14} />} onClick={() => prefill(row.original)}>
            Prefill
          </Button>
          {(row.original.suggested_action === 'create-secondary' ||
            (row.original.suggested_action === 'needs-service' && !!rowOrg(row.original))) && (
            <Button
              variant="ghost"
              size="sm"
              disabled={createSvc.isPending}
              onClick={() => askCreateSecondary(row.original)}
            >
              Create svc
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* ── Onboard form ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Onboard domain(s)</CardTitle>
          <CardDescription>Pick org + CDN service, enter one or more domains, then attach.</CardDescription>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Organization" description="Search then pick the owning org.">
              {() => (
                <Combobox
                  options={orgOptions}
                  value={orgId || null}
                  onChange={(v) => { setOrgId(v ?? ''); setServiceId(''); }}
                  inputValue={orgQuery}
                  onInputChange={setOrgQuery}
                  loading={orgsQ.isLoading}
                  placeholder="Search org by id / name…"
                  searchPlaceholder="Search org by id / name…"
                  emptyHint="Type to search organizations."
                  aria-label="Organization"
                />
              )}
            </Field>

            <Field label="CDN service">
              {() => (
                <Combobox
                  options={serviceOptions}
                  value={serviceId || null}
                  onChange={(v) => setServiceId(v ?? '')}
                  disabled={!orgId}
                  placeholder={orgId ? 'Select CDN service' : 'Pick an org first'}
                  searchPlaceholder="Search CDN service…"
                  emptyHint="No CDN service for this org."
                  aria-label="CDN service"
                />
              )}
            </Field>

            <Field label="Domain(s)" description="One per line, or comma/space separated. Scheme + path are stripped.">
              {(p) => (
                <Textarea
                  {...p}
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  rows={4}
                  placeholder={'cdn.example.com\nassets.example.com'}
                  style={{ fontFamily: 'var(--vn-font-mono)', resize: 'vertical' }}
                />
              )}
            </Field>

            {banner && <Alert tone={banner.tone}>{banner.text}</Alert>}

            {orgHintLoading && (
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                Resolving owner hint…
              </span>
            )}
            {orgHint && !orgHintLoading && (() => {
              const pm = orgHint.provider_matches?.find((m) => m.found);
              const hasSib = orgHint.candidates.length > 0;
              return (
                <Alert tone={pm || hasSib ? 'info' : 'warning'}>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                    {/* Provider lookup — authoritative (vendor knows the owning account) */}
                    {pm ? (
                      <span style={{ fontSize: 'var(--vn-font-size-xs)' }}>
                        Provider (<strong>{pm.provider}</strong>): <strong>{orgHint.domain}</strong> found under vendor org{' '}
                        <strong>{pm.vendor_org_name || '—'}</strong>
                        {pm.organization_id ? (
                          <> → org <strong>{pm.organization_id}</strong></>
                        ) : (
                          ' — verify the customer in DataCentral'
                        )}
                        {pm.status ? ` · ${pm.status}` : ''}
                      </span>
                    ) : (
                      <span style={{ fontSize: 'var(--vn-font-size-xs)' }}>
                        Provider: <strong>{orgHint.domain}</strong> not found on vEdge/raven.
                      </span>
                    )}
                    {/* Sibling-root heuristic — kept as a secondary hint */}
                    {hasSib ? (
                      <>
                        <span style={{ fontSize: 'var(--vn-font-size-xs)' }}>
                          Siblings (root <code>{orgHint.root}</code>):
                        </span>
                        {orgHint.candidates.map((c) => (
                          <span key={c.organization_id} style={{ fontSize: 'var(--vn-font-size-xs)' }}>
                            org <strong>{c.organization_id}</strong> · {c.sibling_domains} sibling(s):{' '}
                            {c.sample_siblings.join(', ')}
                            {c.cdn_services.length > 0 &&
                              ` · services: ${c.cdn_services
                                .map((s) => `${s.provider} — ${s.service_name}`)
                                .join('; ')}`}
                          </span>
                        ))}
                      </>
                    ) : (
                      !pm && (
                        <span style={{ fontSize: 'var(--vn-font-size-xs)' }}>
                          No sibling match (root <code>{orgHint.root}</code>) — likely a new customer; ensure a CDN
                          service exists first.
                        </span>
                      )
                    )}
                  </div>
                </Alert>
              );
            })()}

            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
                {attach.isPending ? 'Attaching…' : `Attach ${domains.length || ''} domain(s)`}
              </Button>
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                Bind-only · no vendor call · sync picks it up on next run.
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Detected gaps ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Detected gaps</CardTitle>
          <CardDescription>From the onboarding-gap detector. Prefill drops a row into the form above.</CardDescription>
        </CardHeader>
        <CardBody>
          <Toolbar style={{ marginBottom: 'var(--vn-space-3)' }}>
            <Select
              value={provider === '' ? 'all' : provider}
              onValueChange={(v) => setProvider(v === 'all' ? '' : (v as ProviderFilter))}
            >
              <SelectTrigger aria-label="Provider filter" style={{ width: 200 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Toolbar>

          {gapsQ.isLoading ? (
            <TabLoading rows={6} />
          ) : gapsQ.isError ? (
            <Alert tone="danger">
              Failed to load gaps.{' '}
              <Button variant="ghost" size="sm" onClick={() => gapsQ.refetch()}>Retry</Button>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState title="No gaps" description="No unresolved CDN onboarding gaps." />
          ) : (
            <>
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                {rows.length} unresolved gap(s).
              </span>
              <DataTable columns={columns} data={rows} />
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={!!csTarget}
        onOpenChange={(o) => {
          if (!o) setCsTarget(null);
        }}
        title="Create CDN service & bind"
        description={
          csTarget
            ? `Create a ${csTarget.provider} service for org ${rowOrg(csTarget)} and bind ${csTarget.domain}? Bind-only — no vendor call.`
            : ''
        }
        confirmLabel="Create & bind"
        loading={createSvc.isPending}
        onConfirm={confirmCreateSecondary}
      />
    </div>
  );
}
